import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HardHatIcon, FactoryIcon, HeartPulseIcon, WheatIcon, WifiIcon, ZapIcon, ShoppingBagIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
export function Sectors() {
  useSEO({
    title: 'Sectors We Serve',
    description: 'Institutional procurement sourcing across construction, industrial equipment, medical supplies, agricultural products, ICT, energy, and consumer goods. Verified suppliers worldwide.',
    path: '/sectors'
  });
  const sectors = [{
    icon: HardHatIcon,
    title: 'Construction Materials',
    subcategories: ['Cement and aggregates', 'Steel and reinforcement bars', 'Prefabricated structures', 'Building machinery and equipment', 'Roofing and cladding materials', 'Plumbing and electrical supplies'],
    context: 'Public works programmes, urban infrastructure tenders, housing development projects, and commercial construction initiatives across African markets.'
  }, {
    icon: FactoryIcon,
    title: 'Industrial Equipment',
    subcategories: ['Manufacturing machinery', 'Processing equipment', 'Industrial tools and components', 'Factory automation systems', 'Material handling equipment', 'Quality control instruments'],
    context: 'Industrial development programmes, manufacturing sector support initiatives, and private sector expansion projects requiring production equipment.'
  }, {
    icon: HeartPulseIcon,
    title: 'Medical Supplies',
    subcategories: ['Pharmaceuticals and medicines', 'Diagnostic equipment', 'Hospital furniture and fixtures', 'Surgical instruments', 'Medical consumables', 'Laboratory equipment'],
    context: 'National health programmes, hospital construction and equipment tenders, primary healthcare initiatives, and disease control programmes.'
  }, {
    icon: WheatIcon,
    title: 'Agricultural Products',
    subcategories: ['Farm machinery and tractors', 'Irrigation systems', 'Fertilizers and agrochemicals', 'Agro-processing equipment', 'Storage and handling systems', 'Greenhouse and horticulture supplies'],
    context: 'Agricultural modernisation programmes, food security initiatives, rural development projects, and commercial farming expansion.'
  }, {
    icon: WifiIcon,
    title: 'ICT & Telecommunications',
    subcategories: ['Network infrastructure equipment', 'Mobile devices and accessories', 'Server and data center equipment', 'Telecommunications towers', 'Fiber optic cables', 'Software and licensing'],
    context: 'Digital transformation initiatives, telecommunications infrastructure expansion, e-government programmes, and connectivity projects.'
  }, {
    icon: ZapIcon,
    title: 'Energy & Infrastructure',
    subcategories: ['Power generation equipment', 'Solar panels and systems', 'Electrical transformers', 'Distribution infrastructure', 'Energy storage systems', 'Grid management equipment'],
    context: 'Electrification programmes, renewable energy projects, grid expansion initiatives, and off-grid power solutions for rural areas.'
  }, {
    icon: ShoppingBagIcon,
    title: 'Consumer & Commercial Goods',
    subcategories: ['FMCG, food, and beverages', 'Household, personal care, and hygiene products', 'Apparel, textiles, and homeware', 'Small appliances and consumer electronics', 'Office furniture, equipment, and stationery', 'Hospitality, educational, and institutional supplies', 'Commercial vehicles and general merchandise'],
    context: 'Bulk and wholesale supply for hypermarkets, supermarkets, retail chains, and distributors, alongside institutional procurement for government offices, schools, hospitality operators, and commercial sector buyers.'
  }];
  return <div className="pt-24" data-id="element-603">
      {/* Hero Band */}
      <section className="py-20 px-6 bg-navy-light" data-id="element-604">
        <div className="max-w-4xl mx-auto text-center" data-id="element-605">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-606">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-607">
              <div className="w-12 h-px bg-gold" data-id="element-608"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-609">
                What We Source
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-610"></div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-semibold text-white mb-6" data-id="element-611">
              Sectors We Serve
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-612">
              Comprehensive sourcing capabilities across all major institutional
              and commercial procurement sectors
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sectors Detail */}
      {sectors.map((sector, i) => <section key={sector.title} className={`py-20 px-6 ${i % 2 === 0 ? 'bg-navy-dark' : 'bg-navy-light'}`} data-id="element-613">
          <div className="max-w-7xl mx-auto" data-id="element-614">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-615">
              <div className="flex items-start gap-6 mb-8" data-id="element-616">
                <div className="w-16 h-16 bg-gold/10 border border-gold/30 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-617">
                  <sector.icon size={32} className="text-gold" data-id="element-618" />
                </div>
                <div data-id="element-619">
                  <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-4" data-id="element-620">
                    {sector.title}
                  </h2>
                  <p className="text-slate-400 text-lg leading-relaxed" data-id="element-621">
                    {sector.context}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-622">
                {sector.subcategories.map(sub => <div key={sub} className="bg-navy-light border border-slate-800 px-4 py-3 rounded-lg" data-id="element-623">
                    <div className="flex items-center gap-2" data-id="element-624">
                      <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" data-id="element-625"></div>
                      <span className="text-slate-300 text-sm" data-id="element-626">{sub}</span>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </section>)}

      {/* CTA */}
      <section className="py-20 px-6 bg-navy-dark" data-id="element-627">
        <div className="max-w-4xl mx-auto text-center" data-id="element-628">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-629">
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6" data-id="element-630">
              Supply Products in These Sectors?
            </h2>
            <p className="text-slate-400 text-lg mb-8" data-id="element-631">
              Complete our pre-qualification form to be considered for relevant
              procurement opportunities
            </p>
            <Link to="/supplier-registration" className="inline-block bg-gold hover:bg-gold-dark text-navy-dark px-8 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-632">
              Pre-Qualification Form
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}